import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { Avatar } from '../Avatar';
import { AgingBadge } from '../work-queue/AgingBadge';
import { PriorityPill } from '../work-queue/priority/PriorityPill';
import { buildWorkQueueCaseUrl } from '../../utils/workQueueLink';
import type { WorkQueueCase } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';

// The Work Queue's assignee filter stores DISPLAY values in the URL and
// resolves them to emails client-side, with 'Me' mapped to the signed-in
// user's address (see resolvedAssignees in pages/WorkQueue.tsx). So the
// "see all" links filter by the 'Me' sentinel rather than an email — they
// survive being shared or bookmarked by someone else, and need no knowledge
// of the current user here.
export const MY_QUEUE_URL = '/work-queue?assignee=Me';

const MS_PER_DAY = 86_400_000;

function ageDaysFromIso(iso: string | null): number {
    if (!iso) return 0;
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return 0;
    return Math.max(0, Math.floor((Date.now() - ts) / MS_PER_DAY));
}

function CaseRow({ workCase }: { workCase: WorkQueueCase }) {
    // Step-entry time is the honest age of the work in front of me; updatedAt
    // is the fallback for rows predating the timeline backfill.
    const ageDays = ageDaysFromIso(workCase.stepEnteredAt ?? workCase.updatedAt);
    const catalog = useStatusCatalog();
    const statusDisplay = catalog.display(workCase.statusId);

    return (
        <Link
            to={buildWorkQueueCaseUrl(workCase.id)}
            className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
        >
            <div className="w-8 h-8 flex-shrink-0">
                <Avatar
                    user={{ name: workCase.name, email: '' }}
                    imageURL={
                        workCase.profileThumbnailUrl ?? workCase.profilePictureUrl ?? undefined
                    }
                />
            </div>
            <div className="flex-1 min-w-0 flex flex-col leading-tight">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-[13px] font-semibold text-gray-800">
                        {workCase.name}
                    </span>
                    <PriorityPill priority={workCase.priority} />
                </div>
                <Tooltip content={statusDisplay}>
                    <span className="truncate text-[11px] text-gray-500 mt-0.5">
                        {statusDisplay}
                        {workCase.practice ? ` · ${workCase.practice}` : ''}
                    </span>
                </Tooltip>
            </div>
            <div className="flex-shrink-0">
                <AgingBadge days={ageDays} />
            </div>
        </Link>
    );
}

interface MyCasesListProps {
    cases: WorkQueueCase[];
    total: number;
    isLoading: boolean;
}

// "What is on my plate", priority-first. Deliberately read-only: acting on a
// case happens in the Work Queue, and every row deep-links straight to it, so
// this panel never becomes a second place where assignment rules live.
export function MyCasesList({ cases, total, isLoading }: MyCasesListProps) {
    if (isLoading) {
        return (
            <div className="flex flex-col">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-5 py-2.5">
                        <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                            <div className="h-3 w-1/3 bg-gray-200 rounded animate-pulse" />
                            <div className="h-2.5 w-1/2 bg-gray-100 rounded animate-pulse" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (cases.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 text-center px-6 py-16">
                <CheckCircleIcon className="h-8 w-8 text-gray-300" strokeWidth={1} />
                <p className="text-sm text-gray-400">Nothing assigned to you right now.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            {cases.map((workCase) => (
                <CaseRow key={workCase.id} workCase={workCase} />
            ))}
            {total > cases.length && (
                <Link
                    to={MY_QUEUE_URL}
                    className="px-5 py-2.5 text-[11px] text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors text-center"
                >
                    Showing the {cases.length} most urgent of {total}. See all
                </Link>
            )}
        </div>
    );
}
